import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/lib/api';
import { toast } from 'react-toastify';

export const useGameLaunch = () => {
  const { isAuthenticated } = useAuth();
  const router = useRouter();
  const [launchingGames, setLaunchingGames] = useState<Set<number>>(new Set());

  // Helper function to check if a game can be launched
  const canLaunchGame = (game: any): boolean => {
    return !!(game.g_code || game.p_code || game.cate_code || (game.active && game.id));
  };

  // Mock game launch function for when backend is not available
  const mockGameLaunch = (game: any) => {
    const demoGameUrls = [
      'https://www.slotomania.com/',
      'https://www.pragmaticplay.com/en/slots-demo/',
      'https://www.netent.com/en/slots/',
      'https://www.playn.go/slots/',
      'https://www.igt.com/games/slots/'
    ];
    
    const selectedUrl = demoGameUrls[game.id % demoGameUrls.length];
    const mockGameUrl = `${selectedUrl}?demo=true&game=${encodeURIComponent(game.name)}&id=${game.id}`;
    
    try {
      const newWindow = window.open(mockGameUrl, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.focus();
        toast.success(`🎮 Opening ${game.name} in demo mode...`);
      } else {
        window.location.href = mockGameUrl;
      }
    } catch (error) {
      window.location.href = mockGameUrl;
    }
  };

  // Handle game launch
  const handleGameLaunch = async (game: any) => {
    if (!isAuthenticated) {
      toast.warning('Please login to play games');
      router.push('/login');
      return;
    }

    try {
      setLaunchingGames(prev => new Set(prev).add(game.id));
      
      if (!canLaunchGame(game)) {
        toast.info(`${game.name} is not available for direct launch. Redirecting to games page...`);
        setTimeout(() => router.push('/games'), 1500);
        return;
      }

      // Try the real API first
      try {
        const launchData = {
          product_id: game.p_code || game.provider_id?.toString() || game.id.toString(),
          game_type: game.cate_code || 'slot',
          game_id: game.g_code || game.id.toString(),
          platform: 'web'
        };
        
        const response = await apiService.launchGame(launchData);

        if (response.success && response.data) {
          const gameUrl = response.data.game_url || (response.data as any).url || (response.data as any).launch_url;
          
          if (gameUrl) {
            try {
              const newWindow = window.open(gameUrl, '_blank', 'noopener,noreferrer');
              
              if (newWindow) {
                newWindow.focus();
                toast.success(`🎮 Opening ${game.name}...`);
                return;
              } else {
                window.location.href = gameUrl;
                return;
              }
            } catch (error) {
              window.location.href = gameUrl;
              return;
            }
          } else {
            throw new Error('No game URL in response');
          }
        } else {
          throw new Error('Invalid API response');
        }
      } catch (apiError) {
        toast.info('🎮 Backend temporarily unavailable. Opening demo mode...');
        mockGameLaunch(game);
        return;
      }
    } catch (error) {
      mockGameLaunch(game);
    } finally {
      setLaunchingGames(prev => {
        const newSet = new Set(prev);
        newSet.delete(game.id);
        return newSet;
      });
    }
  };

  return {
    launchingGames,
    canLaunchGame,
    handleGameLaunch
  };
};
